import React, { useState } from 'react';
import { Mail, Lock, Loader2, X, Heart, Share2, Bell } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';

const CONTEXT_COPY = {
  watchlist: {
    icon: Heart,
    title: 'Save to your Watchlist',
    blurb: 'Subscribe with your email to build your personal watchlist and get our weekly streaming roundup.',
  },
  share: {
    icon: Share2,
    title: 'Share this title',
    blurb: 'Subscribe with your email to share titles with friends and get our weekly streaming roundup.',
  },
  notify: {
    icon: Bell,
    title: 'Get notified on release day',
    blurb: 'Subscribe with your email and we\'ll ping you when this title drops. You\'ll also get our weekly streaming roundup.',
  },
  default: {
    icon: Lock,
    title: 'Sign in to continue',
    blurb: 'Subscribe with your email to unlock Watchlist and Sharing. You\'ll also get our weekly streaming roundup.',
  },
};

const SignInPromptModal = () => {
  const { showPrompt, promptContext, subscribe, completePending, closePrompt } = useSubscription();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  if (!showPrompt) return null;

  const copy = CONTEXT_COPY[promptContext] || CONTEXT_COPY.default;
  const ContextIcon = copy.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setError('');
    try {
      await subscribe(email);
      setStatus('success');
      // Resume the originally-clicked action
      completePending();
      setEmail('');
      setStatus('idle');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Subscription failed. Please try again.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={closePrompt}
      data-testid="signin-prompt-overlay"
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={closePrompt}
          data-testid="signin-prompt-close"
          aria-label="Close"
          className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors z-10"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        <div className="p-8 text-center">
          <div className="inline-flex p-4 bg-[#5DADE2]/20 rounded-full mb-4">
            <ContextIcon className="w-8 h-8 text-[#5DADE2]" />
          </div>

          <h2 className="font-primary text-2xl uppercase tracking-wide text-white mb-3">
            {copy.title}
          </h2>

          <p className="font-secondary text-sm text-gray-400 mb-6 leading-relaxed">
            {copy.blurb}
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                disabled={status === 'loading' || status === 'success'}
                data-testid="signin-prompt-email"
                className="w-full pl-12 pr-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-500 font-secondary focus:outline-none focus:border-[#5DADE2]/60 transition-all disabled:opacity-50"
              />
            </div>

            <button
              type="submit"
              disabled={!email || status === 'loading' || status === 'success'}
              data-testid="signin-prompt-submit"
              className="w-full px-6 py-3 bg-[#5DADE2] hover:bg-[#85C1E9] text-black font-secondary font-bold rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {status === 'loading' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Subscribing...</span>
                </>
              ) : (
                <span>Subscribe & Continue</span>
              )}
            </button>
          </form>

          {error && (
            <p data-testid="signin-prompt-error" className="text-red-400 text-sm mt-3 font-secondary">
              {error}
            </p>
          )}

          <p className="font-secondary text-xs text-gray-500 mt-5">
            No spam, unsubscribe anytime. We respect your privacy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignInPromptModal;
